import { Spinner } from 'baseui/spinner';
import { ClaimListView } from '../ClaimListView/ClaimListView';
import { ViewTypes } from 'structure/multi-asset-staking/types';
import { ClaimCardView } from '../ClaimCardView/ClaimCardView';
import { ClaimActions } from '../ClaimActions/ClaimActions';
import { SetStateAction, useState } from 'react';
import {
  useFilteredClaimableList,
  useClaimableItemState,
} from 'structure/claim/hooks/useFilteredClaimableList';
import { userClaimableList } from 'utils/constants/claim.const';
import { Body2, Normal2 } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { useConnection } from 'wagmi';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';

export const ClaimList = () => {
  const { t } = useTranslation('claim');
  const [view, setView] = useState<ViewTypes>(ViewTypes.CARD_VIEW);
  const { filteredItems, allItemsLoaded, updateItemState } =
    useFilteredClaimableList();
  const { isConnected } = useConnection();
  const csr = useClientRenderDelay();

  const handleView = (newView: SetStateAction<ViewTypes>) => setView(newView);

  const stateCheckers = userClaimableList?.map((item, index) => (
    <ClaimableItemStateChecker
      key={item.projectClaimContractAddress + index}
      item={item}
      index={index}
      onStateChange={updateItemState}
    />
  ));

  if (!userClaimableList || userClaimableList.length === 0) {
    return (
      <div className='m-auto w-full p-8'>
        <Spinner $size='14px' $borderWidth='3px' />
      </div>
    );
  }
  if (!csr) {
    return (
      <div className='m-auto w-full p-8'>
        <Spinner $size='14px' $borderWidth='3px' />
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/50 p-16'>
        <div className='mb-6 flex items-center justify-center'>
          <div className='rounded-full bg-white/5 p-6'>
            <Icon
              icon='claim'
              width={64}
              height={64}
              className='text-white-400'
            />
          </div>
        </div>
        <Normal2 className='mb-2 text-center font-semibold text-white'>
          {t('NO_CLAIMABLE_TOKENS') || 'No claimable tokens available'}
        </Normal2>
        <Body2 className='max-w-md text-center text-white-500'>
          {t('NO_CLAIMABLE_TOKENS_DESCRIPTION') ||
            'There are currently no tokens available to claim. Check back later or connect a different wallet.'}
        </Body2>
      </div>
    );
  }

  if (!allItemsLoaded) {
    return (
      <>
        {stateCheckers}
        <div className='m-auto w-full p-8'>
          <Spinner $size='14px' $borderWidth='3px' />
        </div>
      </>
    );
  }

  const shouldShowEmptyState =
    allItemsLoaded &&
    filteredItems.length === 0 &&
    userClaimableList &&
    userClaimableList.length > 0;

  if (shouldShowEmptyState) {
    return (
      <>
        {stateCheckers}
        <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/50 p-16'>
          <div className='mb-6 flex items-center justify-center'>
            <div className='rounded-full bg-white/5 p-6'>
              <Icon
                icon='claim'
                width={64}
                height={64}
                className='text-white-400'
              />
            </div>
          </div>
          <Normal2 className='mb-2 text-center font-semibold text-white'>
            {t('NO_CLAIMABLE_TOKENS') || 'No claimable tokens available'}
          </Normal2>
          <Body2 className='max-w-md text-center text-white-500'>
            {t('NO_CLAIMABLE_TOKENS_DESCRIPTION') ||
              'There are currently no tokens available to claim. Check back later or connect a different wallet.'}
          </Body2>
        </div>
      </>
    );
  }

  return (
    <>
      {stateCheckers}
      <Body2 className='text-white'>
        Consider staking your claimable tokens to participate in the protocol’s
        long-term growth and unlock staking rewards. Staking can be an efficient
        way to put your tokens to work rather than leaving them idle after
        claiming. If you decide to sell, we encourage you to use a decentralized
        exchange (DEX) instead of a centralized exchange (CEX). Selling through
        a DEX can often result in lower slippage and more favorable execution,
        helping you achieve a better overall price for your tokens.
      </Body2>
      <ClaimActions view={view} handleView={handleView} />

      {ViewTypes.CARD_VIEW === view ? (
        <ClaimCardView claimList={filteredItems} />
      ) : (
        <ClaimListView claimList={filteredItems} />
      )}
    </>
  );
};

function ClaimableItemStateChecker({
  item,
  index,
  onStateChange,
}: {
  item: (typeof userClaimableList)[0];
  index: number;
  onStateChange: (
    index: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: { isLoading: boolean; claimData: any; isButtonEnabled: boolean }
  ) => void;
}) {
  useClaimableItemState(item, index, onStateChange);
  return null;
}
